import { Card, CardBody, Col, Row, Table } from "reactstrap";
import { Link } from "react-router-dom";
import { DateOnly, DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import { formatCurrency } from "utils/currency";
import {
  PaymentExpectationStatus,
  paymentExpectationStatusMap,
} from "../constants/payment-expectation.constants";
import { usePaymentExpectationOverview } from "./PaymentExpectationOverviewContext";
import type { PaymentExpectation } from "../types/payment-expectation.types";
import { Timeline, type TimelineStep, type TimelineStepState } from "components/common/Timeline";

function buildTimeline(exp: PaymentExpectation): TimelineStep[] {
  const history = exp.statusHistory ?? [];
  return history.map((entry, i) => {
    const isLast = i === history.length - 1;
    const label = paymentExpectationStatusMap[entry.status as PaymentExpectationStatus]?.label ?? entry.status;
    const state: TimelineStepState = isLast
      ? entry.status === PaymentExpectationStatus.CANCELLED
        ? "skipped"
        : entry.status === PaymentExpectationStatus.RECEIVED
          ? "done"
          : "active"
      : "done";
    const byLabel = entry.user
      ? [entry.user.firstName, entry.user.lastName].filter(Boolean).join(" ").trim() || `#${entry.by}`
      : entry.by != null ? `#${entry.by}` : undefined;
    return { label, timestamp: entry.at, by: byLabel, actor: entry.message ?? undefined, state };
  });
}

const OverviewTab = () => {
  const { expectation: exp } = usePaymentExpectationOverview();
  if (!exp) return null;

  const balance = Math.max(
    0,
    Number(exp.expectedAmount) - Number(exp.receivedAmount),
  );

  return (
    <Row>
      <Col lg={8}>
        <Card>
          <CardBody>
            <div className="text-muted">
              <h6 className="mb-3 text-uppercase">Collection info</h6>
              <div className="table-responsive">
                <Table className="table-borderless mb-0">
                  <tbody>
                    <tr>
                      <th
                        className="ps-0"
                        scope="row"
                        style={{ width: "180px" }}
                      >
                        Code :
                      </th>
                      <td className="text-muted">{exp.code}</td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Status :
                      </th>
                      <td>
                        <StatusBadge
                          value={exp.status}
                          map={paymentExpectationStatusMap}
                        />
                      </td>
                    </tr>
                    {exp.source ? (
                      <tr>
                        <th className="ps-0" scope="row">
                          Invoice :
                        </th>
                        <td className="text-muted">
                          <Link
                            to={`/finance/invoices/${exp.source.id}`}
                            className="link fw-medium"
                          >
                            {exp.source.code}
                          </Link>
                        </td>
                      </tr>
                    ) : null}
                    {exp.client ? (
                      <tr>
                        <th className="ps-0" scope="row">
                          Client :
                        </th>
                        <td className="text-muted">
                          <Link
                            to={`/clients/${exp.clientId}`}
                            className="link fw-medium"
                          >
                            {exp.client.name}
                          </Link>
                        </td>
                      </tr>
                    ) : null}
                    <tr>
                      <th className="ps-0" scope="row">
                        Expected amount :
                      </th>
                      <td className="text-muted fw-medium">
                        {formatCurrency(Number(exp.expectedAmount))}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Received amount :
                      </th>
                      <td className="text-success fw-medium">
                        {formatCurrency(Number(exp.receivedAmount))}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Balance :
                      </th>
                      <td
                        className={`fw-bold ${balance > 0 ? "text-danger" : "text-success"}`}
                      >
                        {formatCurrency(balance)}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Expected date :
                      </th>
                      <td className="text-muted">
                        <DateOnly date={exp.expectedDate} />
                      </td>
                    </tr>
                    {exp.followUpDate ? (
                      <tr>
                        <th className="ps-0" scope="row">
                          Follow-up date :
                        </th>
                        <td className="text-muted">
                          <DateOnly date={exp.followUpDate} />
                        </td>
                      </tr>
                    ) : null}
                    {exp.promisedBy ? (
                      <tr>
                        <th className="ps-0" scope="row">
                          Promised by :
                        </th>
                        <td className="text-muted">{exp.promisedBy}</td>
                      </tr>
                    ) : null}
                  </tbody>
                </Table>
              </div>

              {exp.remarks ? (
                <div className="pt-3 mt-4">
                  <h6 className="mb-3 text-uppercase">Remarks</h6>
                  <p className="text-muted mb-0">{exp.remarks}</p>
                </div>
              ) : null}

              <div className="pt-3 border-top border-top-dashed mt-4">
                <Row className="gy-3">
                  {exp.created_at && (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">Created</p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={exp.created_at} />
                        </h5>
                      </div>
                    </Col>
                  )}
                  {exp.updated_at && (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Last updated
                        </p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={exp.updated_at} />
                        </h5>
                      </div>
                    </Col>
                  )}
                  <Col lg={3} sm={6}>
                    <div>
                      <p className="mb-2 text-uppercase fw-medium">Status :</p>
                      <h5 className="fs-14 mb-0">
                        <StatusBadge
                          value={exp.status}
                          map={paymentExpectationStatusMap}
                        />
                      </h5>
                    </div>
                  </Col>
                  {exp.cancelledAt ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium text-danger">
                          Cancelled
                        </p>
                        <h5 className="fs-14 mb-0 text-danger">
                          <DateTime date={exp.cancelledAt} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}
                </Row>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>

      <Col lg={4}>
        <Card className="mb-3">
          <CardBody>
            <h6 className="text-uppercase mb-3 text-muted">Timeline</h6>
            <Timeline steps={buildTimeline(exp)} />
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default OverviewTab;
